export const PLATFORMER_SCREEN_WIDTH = 240;
export const PLATFORMER_SCREEN_HEIGHT = 160;
export const PLATFORMER_FIXED_STEP = 1 / 60;

export type PlatformerIntent =
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'jump'
  | 'run'
  | 'start'
  | 'select';

export type PlatformerPhase =
  | 'off'
  | 'boot'
  | 'no-cartridge'
  | 'title'
  | 'playing'
  | 'paused'
  | 'dying'
  | 'level-complete'
  | 'game-over'
  | 'victory';

export type PlatformerEventType =
  | 'power-on'
  | 'power-off'
  | 'boot-logo'
  | 'no-cartridge'
  | 'cartridge-inserted'
  | 'cartridge-removed'
  | 'game-start'
  | 'pause'
  | 'resume'
  | 'select'
  | 'jump'
  | 'land'
  | 'block-hit'
  | 'coin'
  | 'stomp'
  | 'damage'
  | 'death'
  | 'respawn'
  | 'time-warning'
  | 'level-complete'
  | 'level-start'
  | 'game-over'
  | 'victory';

export interface PlatformerEvent {
  type: PlatformerEventType;
  simulationTime: number;
  data?: Readonly<Record<string, number | string | boolean>>;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StageCanvasMetadata {
  width: number;
  height: number;
  tileSize: number;
  pixelScale: number;
  topLeftAnchor: true;
}

export type TileKind = 'ground-top' | 'ground-fill' | 'brick-platform' | 'stair-block';

export interface TileRun {
  id: string;
  tile: TileKind;
  x: number;
  y: number;
  length: number;
  collision: 'solid';
  renderLayer: 'playfield';
}

export type BlockKind = 'brick' | 'question';

export interface BlockSpawn {
  id: string;
  kind: BlockKind;
  x: number;
  y: number;
  contains: 'coin' | 'empty';
  collision: 'solid';
  renderLayer: 'interactive';
}

export interface PipeSpawn {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  collision: 'solid';
  renderLayer: 'interactive';
}

export interface CoinSpawn {
  id: string;
  x: number;
  y: number;
  renderLayer: 'pickups';
}

export interface EnemySpawn {
  id: string;
  kind: 'goomba';
  x: number;
  y: number;
  patrolSpeed: number;
  activationPadding: number;
  renderLayer: 'actors';
}

export type ScenicKind = 'cloud' | 'hill' | 'bush' | 'castle' | 'flagpole';

export interface ScenicObject {
  id: string;
  kind: ScenicKind;
  x: number;
  y: number;
  variant: number;
  parallax: number;
  renderLayer: 'far-bg' | 'mid-bg' | 'near-bg';
  collision: 'none';
}

export interface CheckpointHook {
  id: string;
  triggerX: number;
  spawnX: number;
  spawnY: number;
}

export interface ParallaxLayerMetadata {
  id: 'sky' | 'far-bg' | 'near-bg';
  assetId: 'sky-layer' | 'far-bg-layer' | 'near-bg-layer';
  sourceSize: { width: 240; height: 160 };
  displaySize: { width: 240; height: 160 };
  anchor: 'top-left';
  renderOrder: number;
  scrollFactor: number;
  repeatPolicy: 'repeat-x';
  collisionSource: false;
}

export interface LevelDefinition {
  id: string;
  worldLabel: string;
  name: string;
  mapMode: 'side_scroll_mode';
  mapKind: 'platformer';
  visualModel: 'layered_tilemap';
  runtimeObjectModel: readonly [
    'platform_objects',
    'interactive_scene_objects',
    'scene_hooks',
  ];
  collisionModel: 'tile_collision+explicit_shapes+trigger_zones';
  engineTarget: 'raw_canvas';
  stageCanvas: StageCanvasMetadata;
  world: {
    widthTiles: number;
    heightTiles: number;
    backgroundColor: string;
    timeLimit: number;
  };
  parallaxLayers: readonly ParallaxLayerMetadata[];
  layers: {
    tileRuns: readonly TileRun[];
    blocks: readonly BlockSpawn[];
    pipes: readonly PipeSpawn[];
    coins: readonly CoinSpawn[];
    enemies: readonly EnemySpawn[];
    scenery: readonly ScenicObject[];
  };
  collision: {
    source: 'tile-runs-and-explicit-object-shapes';
    tileSize: number;
    pitKillY: number;
  };
  sceneHooks: {
    playerSpawn: { x: number; y: number };
    checkpoints: readonly CheckpointHook[];
    cameraBounds: Rect;
    finishZone: Rect;
    exitLink: string;
  };
}

export type SpriteAssetId =
  | 'mario-idle'
  | 'mario-run'
  | 'mario-jump'
  | 'mario-fall'
  | 'mario-death'
  | 'goomba-walk'
  | 'goomba-squashed'
  | 'coin'
  | 'tileset'
  | 'cloud'
  | 'hill'
  | 'bush'
  | 'castle'
  | 'flagpole'
  | 'sky-layer'
  | 'far-bg-layer'
  | 'near-bg-layer'
  | 'ground-left'
  | 'ground-middle'
  | 'ground-right'
  | 'ground-stair'
  | 'brick-block'
  | 'question-block'
  | 'used-block'
  | 'pipe';

export interface SpriteAssetDefinition {
  id: SpriteAssetId;
  src: string;
  columns: number;
  rows: number;
  frameCount: number;
  frameWidth?: number;
  frameHeight?: number;
  chromaKeyMagenta?: boolean;
}

export type SpriteAssetManifest = Readonly<Record<SpriteAssetId, SpriteAssetDefinition>>;

export interface PlatformerInputSnapshot {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jump: boolean;
  run: boolean;
  start: boolean;
  select: boolean;
}

export interface PlatformerDiagnostics {
  phase: PlatformerPhase;
  powered: boolean;
  cartridgeInserted: boolean;
  simulationTime: number;
  fixedStepCount: number;
  droppedFrameTime: number;
  levelId: string;
  levelIndex: number;
  score: number;
  coins: number;
  lives: number;
  timer: number;
  cameraX: number;
  player: {
    x: number;
    y: number;
    velocityX: number;
    velocityY: number;
    grounded: boolean;
    animation: string;
    animationFrame: number;
    runCycle: number;
  };
  entities: {
    enemiesAlive: number;
    coinsRemaining: number;
    activeBursts: number;
  };
  input: PlatformerInputSnapshot;
}

export interface PlatformerDebugState {
  level: LevelDefinition;
  diagnostics: PlatformerDiagnostics;
  checkpoint: { x: number; y: number; triggerX: number };
  solids: readonly (Rect & { id: string; kind: 'tile' | 'block' | 'pipe' })[];
  blocks: readonly (BlockSpawn & { used: boolean; bumpTimer: number })[];
  coins: readonly (CoinSpawn & { collected: boolean })[];
  enemies: readonly (EnemySpawn & {
    x: number;
    y: number;
    velocityX: number;
    velocityY: number;
    active: boolean;
    state: 'alive' | 'squashed' | 'dead';
  })[];
  assets: Readonly<Record<SpriteAssetId, 'idle' | 'loading' | 'ready' | 'failed'>>;
}

export interface PlatformerEngineOptions {
  canvas?: HTMLCanvasElement;
  enableKeyboard?: boolean;
  keyboardTarget?: Window | HTMLElement;
  assetManifest?: Partial<SpriteAssetManifest>;
  onEvent?: (event: PlatformerEvent) => void;
  autoRender?: boolean;
}
